import { ActionIcon, Button, Tooltip } from '@mantine/core';
import { Icon, variants } from '@uag/react-core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Rnd } from 'react-rnd';
import { useShallow } from 'zustand/shallow';

import { shortcutList } from 'app/modules/wallforming/components/scenes/toolbar/shortcuts/shortcutList';
import { Shortcuts } from 'app/modules/wallforming/components/scenes/toolbar/shortcuts/Shortcuts';
import { ShortcutSection } from 'app/modules/wallforming/components/scenes/toolbar/types/shortcutSection';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

const screenWidth = window.screen.width;

type ShortcutSectionItem = {
    key: ShortcutSection;
    label: string;
    icon: ShortcutSection;
};

const windowWidth = 820;
const windowHeight = 580;

export const ShortcutPanel = () => {
    const { t } = useTranslation();
    const { isShortcutsPanelOpen, selectedSection, setIsShortcutsPanelOpen, setSelectedShortcutSection } =
        usePlacementStore(
            useShallow((state) => ({
                isShortcutsPanelOpen: state.isShortcutsPanelOpen,
                selectedSection: state.selectedShortcutSection,
                setIsShortcutsPanelOpen: state.setIsShortcutsPanelOpen,
                setSelectedShortcutSection: state.setSelectedShortcutSection
            }))
        );

    const sections = useMemo<ShortcutSectionItem[]>(
        () =>
            Object.values(ShortcutSection).map((section) => ({
                key: section,
                label: t(shortcutList[section].labelKey),
                icon: section
            })),
        [t]
    );

    const activeSection = sections.find((section) => section.key === selectedSection) ?? sections[0];

    return (
        <>
            <Tooltip
                disabled={isShortcutsPanelOpen}
                label={t('labelShortcuts')}
                position="right"
                variant={variants.tooltip.right}
            >
                <ActionIcon
                    className={`
                        bg-background-gray-ultra-light hover:bg-background-gray! pointer-events-auto
                        ${screenWidth >= 1920 ? 'action-icon-large' : 'action-icon-medium'}
                    `}
                    onClick={() => setIsShortcutsPanelOpen(true)}
                >
                    help
                </ActionIcon>
            </Tooltip>

            <div
                style={{
                    height: '100vh',
                    left: 0,
                    pointerEvents: isShortcutsPanelOpen ? 'auto' : 'none',
                    position: 'fixed',
                    top: 0,
                    width: '100vw',
                    zIndex: 9999,
                    visibility: isShortcutsPanelOpen ? 'visible' : 'hidden'
                }}
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onPointerMove={(e) => e.stopPropagation()}
                onPointerUp={(e) => e.stopPropagation()}
            >
                <Rnd
                    bounds="window"
                    default={{
                        x: Math.max((window.innerWidth - windowWidth) / 2, 0),
                        y: Math.max((window.innerHeight - windowHeight) / 2, 0),
                        width: windowWidth,
                        height: windowHeight
                    }}
                    dragHandleClassName="rnd-drag-handle"
                    enableResizing={false}
                    enableUserSelectHack={false}
                    size={{ width: windowWidth, height: windowHeight }}
                    style={{ pointerEvents: 'auto' }}
                >
                    <div
                        style={{
                            background: '#ffffff',
                            border: '1px solid #e5e7eb',
                            borderRadius: 8,
                            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.18)',
                            color: '#374151',
                            display: 'flex',
                            flexDirection: 'column',
                            fontFamily: 'inherit',
                            fontSize: 12,
                            height: windowHeight,
                            pointerEvents: 'auto',
                            userSelect: 'none',
                            width: windowWidth
                        }}
                    >
                        {/* Header */}
                        <div
                            className="rnd-drag-handle"
                            style={{
                                alignItems: 'center',
                                background: '#f3f4f6',
                                borderBottom: '1px solid #e5e7eb',
                                borderRadius: '6px 6px 0 0',
                                cursor: 'move',
                                display: 'flex',
                                justifyContent: 'space-between',
                                padding: '6px 10px'
                            }}
                        >
                            <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: 0.5 }}>
                                {t('labelShortcuts')}
                            </span>

                            <ActionIcon
                                className="action-icon-small bg-background-gray-ultra-light hover:bg-background-gray! pointer-events-auto icon-sm"
                                onClick={() => setIsShortcutsPanelOpen(false)}
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                close
                            </ActionIcon>
                        </div>

                        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
                            {/* Menu */}
                            <div
                                style={{
                                    background: '#f8fafc',
                                    borderRight: '1px solid #e5e7eb',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 4,
                                    padding: 10,
                                    width: 220
                                }}
                            >
                                {sections.map((section) => (
                                    <Button
                                        key={section.key}
                                        className="flex items-center rounded-md px-1 py-2 text-left text-sm transition-colors w-full cursor-pointer"
                                        style={{
                                            backgroundColor:
                                                selectedSection === section.key ? '#e2e8f0' : 'transparent',
                                            color: '#334155'
                                        }}
                                        type="button"
                                        onClick={() => setSelectedShortcutSection(section.key)}
                                        onMouseDown={(e) => e.stopPropagation()}
                                    >
                                        <Icon
                                            className="action-menu-entry-decoration action-menu-entry-decoration-icon mr-1.5"
                                            icon={section.icon}
                                        />
                                        <span className="min-w-0 truncate">{section.label}</span>
                                    </Button>
                                ))}
                            </div>

                            <div className="flex min-w-0 flex-1 flex-col overflow-hidden p-4">
                                <div className="min-h-0 flex-1 overflow-auto rounded-md border border-border-ultra-light bg-background-default p-3 shadow-sm">
                                    <Shortcuts section={activeSection.key} />
                                </div>
                            </div>
                        </div>
                    </div>
                </Rnd>
            </div>
        </>
    );
};
